import React, { useCallback } from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";

import styles from "./EntityPinSettings.module.scss";
import PinColorSelectField from "./PinColorSelectField";
import PinShapeSelectField from "./PinShapeSelectField";

interface Props {
  entityType: EntityTypesSupportedByMapsPage;
  onCreateCustomColorLegend?: (entityType: EntityTypesSupportedByMapsPage) => void;
  onCreateCustomShapeLegend?: (entityType: EntityTypesSupportedByMapsPage) => void;
}

const EntityPinSettings: React.FC<Props> = ({
  entityType,
  onCreateCustomColorLegend,
  onCreateCustomShapeLegend,
}) => {
  const intl = useIntl();

  const handleCreateCustomColorLegend = useCallback(
    () => onCreateCustomColorLegend?.(entityType),
    [entityType, onCreateCustomColorLegend]
  );
  const handleCreateCustomShapeLegend = useCallback(
    () => onCreateCustomShapeLegend?.(entityType),
    [entityType, onCreateCustomShapeLegend]
  );

  return (
    <div className={styles.container}>
      <span className={styles.title}>
        {intl.formatMessage(
          {
            id: "map.toolbar.popovers.pinSettings.entitySettings.title",
            defaultMessage:
              "{entityType, select, accounts {Company pins} contacts {Person pins} deals {Deal pins} other {Record pins}}",
            description: "Title of the entity pin settings in Pin Settings popover on the map page",
          },
          {
            entityType,
          }
        )}
      </span>
      <div className={cn(styles.settings, styles[entityType])}>
        <PinColorSelectField
          className={styles.selectField}
          entityType={entityType}
          label={intl.formatMessage(
            {
              id: "map.toolbar.popovers.pinSettings.entitySettings.colorSelect.label",
              defaultMessage:
                "{entityType, select, accounts {Set <b>pin color</b> of Companies based on} contacts {Set <b>pin color</b> of People based on} deals {Set <b>pin color</b> of Deals based on} other {Set <b>pin color</b> of Records based on}}",
              description:
                "Title of the Color select field in Pin Settings popover on the map page",
            },
            {
              b: (texts: string[]) => <b>{texts}</b>,
              entityType,
            }
          )}
          onCreateCustom={handleCreateCustomColorLegend}
        />
        <PinShapeSelectField
          className={styles.selectField}
          entityType={entityType}
          label={intl.formatMessage(
            {
              id: "map.toolbar.popovers.pinSettings.entitySettings.shapeSelect.label",
              defaultMessage:
                "{entityType, select, accounts {Set <b>shape</b> of Companies based on} contacts {Set <b>shape</b> of People based on} deals {Set <b>shape</b> of Deals based on} other {Set <b>shape</b> of Records based on}}",
              description:
                "Title of the Shape select field in Pin Settings popover on the map page",
            },
            {
              b: (texts: string[]) => <b>{texts}</b>,
              entityType,
            }
          )}
          onCreateCustom={handleCreateCustomShapeLegend}
        />
      </div>
    </div>
  );
};

export default EntityPinSettings;
